// 批改細節頁(Feedback)「上一位／下一位學生」切換的純邏輯(kanban 106957 R5)。
//
// 高中批改細節頁的資料全放 sessionStorage、畫面直接讀 workflowResult 渲染,
// 沒有國中 demo-feedback 那種單一 applyBatchItem。這支檔把「換學生」會用到的
// 幾件純邏輯抽出來(清單建置、當前筆定位、邊界切換、UI 禁用判斷),頁面層只
// 負責 sessionStorage 寫回/重新解析等副作用。全部不碰 DOM 也不打 API → vitest 直測。

// 批改細節頁真正會用到的 workflowResult 欄位子集(與 ClassFeedback.handleViewRecord
// 寫入 sessionStorage 的那組一致)。切換學生時整包當成新的 workflowResult 寫回,
// 所以欄位要齊全到足以獨立驅動整頁(學生資訊/題目/圖片/評分解析)。
export interface FeedbackSiblingItem {
  _id: string;
  classCode: string;
  correctingResult: string; // 一律 JSON 字串:細節頁 getTeacherEditContext 要求 typeof === "string"
  bookID: string;
  chapterID: string;
  studentName: string;
  studentSeatNo: string;
  studentInput: string;
  studentInputUrl: string;
  studentInputOriginUrl?: string;
  className?: string;
  markdown?: string;
}

// buildFeedbackSibling 的輸入:一筆 classrecord。correctingResult 可能是物件(後端原樣)
// 或已序列化字串;只挑我們要的欄位,不綁完整 Record 型別以便單測。
export interface FeedbackRecordLike {
  _id: string;
  classCode?: string;
  correctingResult: unknown;
  bookID: string;
  chapterID: string;
  studentName: string;
  studentSeatNo: string;
  studentInput: string;
  studentInputUrl: string;
  studentInputOriginUrl?: string;
  className?: string;
  markdown?: string;
  createdAt?: string; // 供呼叫端的過期述詞判斷(超過一年的紀錄列表上不給開,也不進切換清單)
}

// 一筆 classrecord → 一筆 sibling(等同國中 buildViewData:單一來源,初載與切換共用形狀)。
// correctingResult 已是字串就原樣帶,是物件才序列化 — 兩種來源都吐出可被細節頁解析的字串
// (handleViewRecord 現行寫法對物件無條件 JSON.stringify;此處對字串放行是嚴格更安全,
//  物件情形位元組相同)。
export function buildFeedbackSibling(
  record: FeedbackRecordLike,
  className?: string
): FeedbackSiblingItem {
  const correctingResult =
    typeof record.correctingResult === "string"
      ? record.correctingResult
      : JSON.stringify(record.correctingResult);

  const item: FeedbackSiblingItem = {
    _id: record._id,
    classCode: record.classCode ?? "",
    correctingResult,
    bookID: record.bookID,
    chapterID: record.chapterID,
    studentName: record.studentName,
    studentSeatNo: record.studentSeatNo,
    studentInput: record.studentInput,
    studentInputUrl: record.studentInputUrl,
    studentInputOriginUrl: record.studentInputOriginUrl,
    className: className ?? record.className,
  };
  // markdown 只在有值時帶(對齊 handleViewRecord 的 ...(record.markdown && {markdown})):
  // 缺 markdown 才會走各章節表格標題的「顯示作文題目」fallback(item 4 的行為)。
  if (record.markdown) item.markdown = record.markdown;
  return item;
}

// 一整份「已由 filterRecordsByType 排好序的同章節清單」→ siblings,順序原樣保留
// (排序責任在頁面的 filterRecordsByType,這裡不再重排,才能保證分母/序號與列表一致)。
//
// isExpired(選填):過期述詞。列表上超過一年的紀錄顯示「已過期」且沒有「檢視紀錄」按鈕,
// 老師無法直接開;切換清單也要一併排除,否則「箭頭切得到、按鈕不給進」不一致,N/M 分母
// 也會多算(kanban 106957 review R5-1)。過濾在建清單當下做,index 反查一律針對過濾後清單,
// 定位仍正確;當前被點開那筆必為非過期(過期沒按鈕),不會被濾掉。
export function buildFeedbackSiblings(
  records: FeedbackRecordLike[],
  className?: string,
  isExpired?: (record: FeedbackRecordLike) => boolean
): FeedbackSiblingItem[] {
  if (!Array.isArray(records)) return [];
  const openable =
    typeof isExpired === "function"
      ? records.filter((record) => !isExpired(record))
      : records;
  return openable.map((record) => buildFeedbackSibling(record, className));
}

// 依 _id 反查當前筆在清單中的位置(重整後用 workflowResult._id 定位;找不到回 -1
// 代表「當前筆不屬於這份清單」,呼叫端應視為無清單、維持單筆檢視)。
export function findSiblingIndex(
  list: FeedbackSiblingItem[],
  currentId: string | null | undefined
): number {
  if (!Array.isArray(list) || !currentId) return -1;
  return list.findIndex((item) => item._id === currentId);
}

// 邊界檢查後算出切換目標;越界回 null(首筆禁上一位、末筆禁下一位)。
// currentIndex < 0(當前筆不在清單)一律回 null,不猜位置。
export function computeSiblingTarget(
  list: FeedbackSiblingItem[],
  currentIndex: number,
  delta: number
): { item: FeedbackSiblingItem; index: number } | null {
  if (!Array.isArray(list) || list.length === 0) return null;
  if (currentIndex < 0) return null;
  const nextIndex = currentIndex + delta;
  if (nextIndex < 0 || nextIndex >= list.length) return null;
  return { item: list[nextIndex], index: nextIndex };
}

// 是否顯示切換列:清單 2 筆以上才顯示(單筆或無清單都不顯示,對齊國中 batchSiblings.length > 1)。
export function shouldShowSwitcher(list: FeedbackSiblingItem[]): boolean {
  return Array.isArray(list) && list.length > 1;
}

// 「上一位」是否可用(= 前面還有一筆);首筆或無清單為 false。
export function canGoPrev(list: FeedbackSiblingItem[], index: number): boolean {
  return computeSiblingTarget(list, index, -1) !== null;
}

// 「下一位」是否可用(= 後面還有一筆);末筆或無清單為 false。
export function canGoNext(list: FeedbackSiblingItem[], index: number): boolean {
  return computeSiblingTarget(list, index, 1) !== null;
}

// 由該筆推導上傳模式(與 handleViewRecord 同規則:有原圖切割網址就是 image,否則 text)。
export function uploadModeForItem(item: FeedbackSiblingItem): "image" | "text" {
  return item.studentInputUrl !== "" ? "image" : "text";
}
